import React from "react";
import { StepShell, EyeResultRow } from "@/components/StepShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { SNELLEN_LINES, ACUITY_OPTIONS, snellenFontPx } from "@/lib/screeningData";
import { useI18n } from "@/i18n";
import { Eye, Ruler, MonitorCheck } from "lucide-react";

export const SnellenTest = ({ data, update }) => {
  const { t, tc } = useI18n();
  const s = data.snellen;
  const pxPerMm = data.calibration?.pxPerMm || null;
  const distanceCm = s.distanceCm || 250;
  const sizeFor = (line) => (pxPerMm ? snellenFontPx(line.value / 10, pxPerMm, distanceCm * 10) : line.size * (distanceCm / 50));
  const distanceLabel = (distanceCm / 100).toFixed(2).replace(/\.?0+$/, "");
  const AcuitySelect = ({ value, onChange, testId }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-48" data-testid={testId}><SelectValue placeholder={t("acuity_ph")} /></SelectTrigger>
      <SelectContent>{ACUITY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{tc(o)}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <StepShell icon={Eye} title={t("sn_title")} subtitle={t("sn_sub")} testId="snellen-step">
      <div className="mb-4 flex items-center gap-2 rounded-md border border-slate-200 bg-white p-3 text-sm" data-testid="calibration-status">
        {pxPerMm ? (
          <><MonitorCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span className="font-medium text-slate-700">{t("sn_calib_ok")} ({pxPerMm.toFixed(2)} px/mm)</span></>
        ) : (
          <><Ruler className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="font-medium text-slate-600">{t("sn_calib_no")}</span></>
        )}
      </div>

      <div className="mb-4 rounded-md border border-slate-200 bg-white p-4" data-testid="snellen-distance-control">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <Ruler className="w-4 h-4" /> {t("sn_distance")}
          </Label>
          <span className="text-sm font-bold text-primary tabular-nums" data-testid="snellen-distance-value">
            {distanceLabel} m ({distanceCm} cm)
          </span>
        </div>
        <Slider data-testid="snellen-distance-slider" min={40} max={300} step={10} value={[distanceCm]}
          onValueChange={(v) => update("snellen", { distanceCm: v[0] })} />
        <p className="text-xs text-slate-500 mt-2">{t("sn_distance_hint")}</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="bg-white px-6 py-10 flex flex-col items-center gap-3">
          {SNELLEN_LINES.map((line) => (
            <div key={line.label} className="flex items-center justify-center w-full">
              <span className="w-14 text-right pr-4 text-xs font-mono text-slate-400 select-none">{line.label}</span>
              <span className="optotype flex-1 text-center" style={{ fontSize: `${sizeFor(line)}px` }}>{line.letters.join(" ")}</span>
              <span className="w-14" />
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 bg-slate-50 p-6">
          <p className="text-sm font-semibold text-slate-800 mb-2">{t("sn_record")}</p>
          <div className="divide-y divide-slate-200">
            <EyeResultRow label={t("eye_od")}><AcuitySelect value={s.od} onChange={(v) => update("snellen", { od: v })} testId="snellen-od-select" /></EyeResultRow>
            <EyeResultRow label={t("eye_os")}><AcuitySelect value={s.os} onChange={(v) => update("snellen", { os: v })} testId="snellen-os-select" /></EyeResultRow>
            <EyeResultRow label={t("eye_ou")}><AcuitySelect value={s.ou} onChange={(v) => update("snellen", { ou: v })} testId="snellen-ou-select" /></EyeResultRow>
          </div>
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("notes")}</Label>
            <Textarea data-testid="snellen-note-input" rows={2} value={s.note}
              onChange={(e) => update("snellen", { note: e.target.value })} placeholder={t("notes_ph")} />
          </div>
        </div>
      </div>
    </StepShell>
  );
};
